import * as strings from "../../strings";
import store from "../../store";
import * as FleetSelectors from "../../store/Fleet/Fleet.selector";
import * as Api from "../../Api";

import * as FormatUtils from "./FormatUtils";
import { FlattenedGroup, Group } from "../types/Group";
import { TextElement } from "./LocaleUtils";
import { Falsy } from "../types/UtilTypes";
import { Targets } from "lib/types/Targets";
import { Fleet } from "lib/types/Fleet";

function iterateGroups(
	flatGroups: FlattenedGroup[],
	groups: Group[],
	depth = 0,
	parentUnit = "",
	parentPath = "",
) {
	if (Array.isArray(groups) && groups.length > 0) {
		groups.forEach((group, index) => {
			const unit =
				parentUnit.length === 0
					? (index + 1).toString()
					: parentUnit + String.fromCharCode(97 + index).toUpperCase();
			const path =
				parentPath.length === 0 ? group.id : parentPath + "." + group.id;

			if (group.flattened) {
				flatGroups.push(group.flattened);
			} else {
				const flatGroup = { ...group, unit, path, depth };
				group.flattened = flatGroup;
				flatGroups.push(flatGroup);
			}

			if (group.groups) {
				iterateGroups(flatGroups, group.groups, depth + 1, unit, path);
			}
		});
	}
}

export const flattenGroups = (
	groups: Group[] | undefined,
): FlattenedGroup[] => {
	if (!groups) return [];
	const flatGroups: FlattenedGroup[] = [];
	iterateGroups(flatGroups, groups);
	return flatGroups;
};

export const findGroupById = (
	groups: Group[] | undefined,
	id: string,
): Group | undefined => {
	if (!groups) return undefined;

	return findGroup(groups, (group) => group.id === id);
};

export const getGroup = (groups: Group[], path: string): Group | undefined => {
	const parts = path.split(".");
	let currentGroups = groups;
	for (const part of parts.slice(0, parts.length - 1)) {
		const partGroup = currentGroups.find((group) => group.id === part);

		if (!partGroup) {
			return undefined;
		}

		currentGroups = partGroup.groups;
	}
	return currentGroups.find((group) => group.id === parts[parts.length - 1]);
};

export const findGroup = (
	groups: Group[],
	predicate: (group: Group) => boolean,
): Group | undefined => {
	const flatGroups: FlattenedGroup[] = [];
	iterateGroups(flatGroups, groups);
	for (const group of flatGroups) {
		if (predicate(group)) {
			const result = getGroup(groups, group.path)!;

			if (!result) {
				return undefined;
			}

			result.unit = group?.unit;
			result.depth = group?.depth;
			return result;
		}
	}
	return undefined;
};

export const findGroupAndSubgroupList = (
	groups: Group[] | undefined,
	id?: string,
): FlattenedGroup[] => {
	if (!groups) return [];
	if (!id) {
		return flattenGroups(groups);
	}

	const group = findGroup(groups, (group) => group.id === id);
	if (!group) return [];
	return [group as any, ...flattenGroups(group.groups || [])];
};

export const findGroupName = (
	fleetId: string | Falsy,
	groupId: string | Falsy,
	anonymize = true,
	fallback: string | number | (() => TextElement) | undefined = undefined,
): TextElement => {
	if (!groupId) {
		return FormatUtils.getFleetDisplayName(fleetId, anonymize);
	}

	let fallbackFunc: () => TextElement;
	if (fallback === undefined) {
		fallbackFunc = () => (anonymize ? strings.MISC_GROUP : groupId);
	} else if (typeof fallback === "function") {
		fallbackFunc = fallback;
	} else {
		fallbackFunc = () => fallback && fallback.toString();
	}

	const state = store.getState();
	const groups = FleetSelectors.selectCurrentScopeFleetGroups(state).value;
	const group = groups && findGroup(groups, (group) => group.id === groupId);
	return group
		? FormatUtils.getGroupDisplayName(group, anonymize)
		: fallbackFunc();
};

export const getGroupTargets = (
	groups: Group[] | undefined,
	groupId: string,
): Targets | undefined | {} => {
	if (!groups) {
		return {};
	}

	const group = findGroupById(groups, groupId);
	return group?.targets;
};

export const findRootGroup = (group?: Group) => {
	if (!group) {
		return null;
	}

	return group.rootGroup ? group : group.rootGroupData;
};

export const checkIfFileManagerDownloadIsEnabledForGroup = (
	fleet: Fleet,
	group?: Group | null | undefined,
	scopedGroups?: Group[] | null | undefined,
) => {
	// If the group has the fileManager disabled specifically
	if (group?.settings?.fileManagerDownload === false) return false;
	if (fleet.settings?.fileManagerDownload) return true;

	if (group) {
		if (group?.settings?.fileManagerDownload) return true;

		// Check the hierarchy of groups to see if any of them have fileManagerDownload enabled
		if (scopedGroups && scopedGroups.length > 0 && group?.path) {
			for (const parentGroup of group.path.split(".")) {
				const group = findGroupById(scopedGroups, parentGroup);
				if (group?.settings?.fileManagerDownload) {
					return true;
				}
			}
		}
	}
	return false;
};

export const checkIfFileManagerUploadIsEnabledForGroup = (
	fleet: Fleet,
	group?: Group | null | undefined,
	scopedGroups?: Group[] | null | undefined,
) => {
	// If the group has the fileManager disabled specifically
	if (group?.settings?.fileManagerUpload === false) return false;
	if (fleet.settings?.fileManagerUpload) return true;

	if (group) {
		if (group?.settings?.fileManagerUpload) return true;

		// Check the hierarchy of groups to see if any of them have fileManagerUpload enabled
		if (scopedGroups && scopedGroups.length > 0 && group?.path) {
			for (const parentGroup of group.path.split(".")) {
				const group = findGroupById(scopedGroups, parentGroup);
				if (group?.settings?.fileManagerUpload) {
					return true;
				}
			}
		}
	}
	return false;
};

export const checkIfVehicleChecksFormIdIsEnabledForGroup = (
	fleet: Fleet | null | undefined,
	group?: Group | null | undefined,
	scopedGroups?: Group[] | null | undefined,
) => {
	if (fleet?.settings?.vehicleChecksFormId)
		return fleet?.settings?.vehicleChecksFormId;

	// if (!group?.settings?.vehicleChecksFormId) return "";

	if (group) {
		if (group?.settings?.vehicleChecksFormId)
			return group?.settings?.vehicleChecksFormId;

		// Check the hierarchy of groups to see if any of them have vehicleChecksFormId enabled
		if (scopedGroups && scopedGroups.length > 0 && group?.path) {
			for (const parentGroup of group.path.split(".")) {
				const group = findGroupById(scopedGroups, parentGroup);
				if (group?.settings?.vehicleChecksFormId) {
					return group?.settings?.vehicleChecksFormId;
				}
			}
		}
	}
	return "";
};

export const fetchGroupSSOStatuses = async (
	emails: string[],
	groupIds?: string[],
) => {
	try {
		const { data } = await Api.fetchCheckGroupSSO.submit({
			query: {
				emails: emails,
				groupIds: groupIds || [],
			},
		});

		return data;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(
			`Error when fetching group sso statuses: ${publicErrMsg || err.message}`,
		);
	}
};
